// The nightly fill-in (Phase 4, Part 2; agreed with Kristi Oct 23 and Oct 25, 2026).
//
// Each night the GitHub job asks the nightly-fill server function for chunks of books. For each
// book this code runs the same lookup as book-lookup and hands its "can fill" list to the
// database, which writes blanks only (migration 0019). Here:
//   * the Google guard: every Google call counts (searches, retries and the daily check); before
//     each book, if fewer than 3 calls are left under the night's limit (400), the night stops;
//   * a chunk stops after about 110 seconds (Supabase stops a function at 150 on the free plan);
//   * Open Library is asked at most about once a second;
//   * the daily check that Google's labelled searches still find nothing (or work again).
// Nothing here writes: the database functions do, and a dry run writes nothing to the books.

import { lookUp, MAX_GOOGLE_CALLS } from '../lookup/lookup.ts';
import { gbByIsbn } from '../lookup/sites.ts';
import { hideKey } from '../lookup/text.ts';
import type { Candidate, Difference, FetchResult, LookupAnswer, StoredWork } from '../lookup/types.ts';

/** Google calls allowed per night (shared allowance about 1,000 a day). */
export const GOOGLE_LIMIT = 400;
/** The most books one chunk takes. */
export const CHUNK_MAX = 25;
/** A chunk stops starting new books after this long. */
export const CHUNK_MS = 110_000;
/** At most about one Open Library call a second. */
export const OPEN_LIBRARY_PACE_MS = 1_000;
/** Dune: the known ISBN for the daily Google check. */
export const CHECK_ISBN = '9780441172719';

export type Outcome = 'filled' | 'nothing_new' | 'not_found' | 'choose' | 'error';

/** One value for the database, in the shape nightly_apply_fills takes. */
export interface FillRow {
  table: 'works' | 'editions';
  row_id: string;
  field: string;
  value: string;
  source: 'Open Library' | 'Google Books';
  with?: Record<string, string>;
}

export interface RunInfo {
  id: string;
  dryRun: boolean;
  googleCalls: number;
  googleLimit: number;
  finished: boolean;
}

export interface ApplyArgs {
  run: string;
  work: string;
  fills: FillRow[];
  outcome: Outcome;
  needs: string[];
  problems: string[];
  olCalls: number;
  gbCalls: number;
}

export interface ApplyResult {
  fields_filled: number;
  covers_added: number;
  skipped: number;
  result: Outcome;
}

/** What the nightly code needs from the database (the server function provides it). */
export interface NightlyDb {
  getRun(runId: string): Promise<RunInfo | null>;
  pick(howMany: number, skip: number): Promise<{ workId: string; shelf: string }[]>;
  /** The book with its editions; 'private' for a private-to-me book (never looked up). */
  loadWork(workId: string): Promise<StoredWork | 'private' | null>;
  apply(args: ApplyArgs): Promise<ApplyResult>;
  /** Keeps what Review needs from the lookup: its possible matches and its differences (0042). */
  record(work: string, candidates: Candidate[], differences: Difference[]): Promise<void>;
}

export interface ChunkDeps {
  db: NightlyDb;
  fetchJson: (url: string) => Promise<FetchResult>;
  googleKey?: string;
  now?: () => number;
  wait?: (ms: number) => Promise<void>;
  chunkMs?: number;
  paceMs?: number;
}

/** Why a chunk stopped: took its books, ran out of time (ask again), nothing left, the Google guard, or an error. */
export type ChunkStop = 'chunk_full' | 'chunk_time' | 'nothing_left' | 'google_guard' | 'error';

export interface ChunkResult {
  lookedUp: number;
  stopped: ChunkStop;
  /** The night's Google calls so far, this chunk included. */
  googleCalls: number;
  books: { title: string; shelf: string; result: Outcome; filled: number }[];
  error?: string;
}

/** The lookup's answer, as the database's result. */
export function outcomeOf(a: LookupAnswer): Outcome {
  if (a.status === 'matched') return a.canFill.length ? 'filled' : 'nothing_new';
  if (a.status === 'choose') return 'choose';
  // "Not found" while a site didn't answer is an error: try again the next night.
  if (a.status === 'not_found') return a.errors.length ? 'error' : 'not_found';
  return 'error';
}

/** The lookup's "can fill" list for the database: only rows already in the catalog. */
export function fillRows(a: LookupAnswer): FillRow[] {
  return a.canFill
    .filter((f) => f.rowId !== null)
    .map((f) => ({
      table: f.table,
      row_id: f.rowId!,
      field: f.field,
      value: String(f.value),
      source: f.source,
      ...(f.with ? { with: f.with } : {}),
    }));
}

/** The "needs you" fields, for Review new books later. */
export function needsOf(a: LookupAnswer): string[] {
  return [...new Set(a.needsYou.map((n) => n.field ?? 'match'))];
}

/** The possible matches Review's Pick the match offers (up to 6; 0042). */
export function candidatesOf(a: LookupAnswer): Candidate[] {
  return a.needsYou.flatMap((n) => n.candidates ?? []).slice(0, 6);
}

/** "Different from yours" for Review (up to 40; 0042). */
export function differencesOf(a: LookupAnswer): Difference[] {
  return a.differs.slice(0, 40);
}

/** One chunk of the night: up to `take` books, in order, under the guard and the time limit. */
export async function runChunk(input: { runId: string; take: number; skip?: number }, deps: ChunkDeps): Promise<ChunkResult> {
  const now = deps.now ?? (() => Date.now());
  const wait = deps.wait ?? ((ms: number) => new Promise<void>((done) => setTimeout(done, ms)));
  const chunkMs = deps.chunkMs ?? CHUNK_MS;
  const paceMs = deps.paceMs ?? OPEN_LIBRARY_PACE_MS;
  const started = now();
  const out: ChunkResult = { lookedUp: 0, stopped: 'chunk_full', googleCalls: 0, books: [] };

  const run = await deps.db.getRun(input.runId);
  if (!run || run.finished) return { ...out, stopped: 'error', error: 'No unfinished run with that ID.' };
  out.googleCalls = run.googleCalls;

  const take = Math.max(1, Math.min(CHUNK_MAX, Math.floor(input.take)));
  // A dry run records nothing per book, so it moves on by skipping the books already done.
  const picked = await deps.db.pick(take, run.dryRun ? Math.max(0, input.skip ?? 0) : 0);
  if (!picked.length) return { ...out, stopped: 'nothing_left' };

  for (const { workId, shelf } of picked) {
    if (now() - started >= chunkMs) return { ...out, stopped: 'chunk_time' };
    if (run.googleLimit - out.googleCalls < MAX_GOOGLE_CALLS) return { ...out, stopped: 'google_guard' };

    const lookupStarted = now();
    let answer: LookupAnswer | null = null;
    let title = '';
    let problems: string[] = [];
    try {
      const w = await deps.db.loadWork(workId);
      if (w === 'private' || w === null) {
        // The database never picks these; skip without recording anything.
        continue;
      }
      title = w.title;
      // The nightly run has no one reader behind it: English for now (Kristi, Oct 26; h047).
      answer = await lookUp({ workId }, { fetchJson: deps.fetchJson, googleKey: deps.googleKey, wait, loadWork: async () => w, languages: ['en'] });
    } catch (e) {
      problems = [hideKey(e instanceof Error ? e.message : String(e), deps.googleKey).slice(0, 300)];
    }

    const outcome: Outcome = answer ? outcomeOf(answer) : 'error';
    const olCalls = answer?.calls.openLibrary ?? 0;
    const gbCalls = answer?.calls.google ?? 0;
    let applied: ApplyResult;
    try {
      applied = await deps.db.apply({
        run: run.id,
        work: workId,
        fills: answer ? fillRows(answer) : [],
        outcome,
        needs: answer ? needsOf(answer) : [],
        problems: answer ? answer.errors.map((e) => hideKey(e, deps.googleKey).slice(0, 300)) : problems,
        olCalls,
        gbCalls,
      });
    } catch (e) {
      out.googleCalls += gbCalls;
      return { ...out, stopped: 'error', error: `Saving “${title}” failed: ${e instanceof Error ? e.message : String(e)}`.slice(0, 500) };
    }
    // What Review needs, replaced each time (not on a dry run, and not on an error: an error
    // keeps what the last good lookup found).
    if (answer && !run.dryRun && outcome !== 'error') {
      try {
        await deps.db.record(workId, candidatesOf(answer), differencesOf(answer));
      } catch (e) {
        out.googleCalls += gbCalls;
        return { ...out, stopped: 'error', error: `Saving Review's notes for “${title}” failed: ${e instanceof Error ? e.message : String(e)}`.slice(0, 500) };
      }
    }
    out.googleCalls += gbCalls;
    out.lookedUp++;
    out.books.push({ title, shelf, result: applied.result, filled: applied.fields_filled });

    // Open Library: at most about one call a second, on average over the book.
    const rest = olCalls * paceMs - (now() - lookupStarted);
    if (rest > 0) await wait(rest);
  }
  return out;
}

// ===== The daily check of Google's labelled searches =====

export type GoogleVerdict = 'still_broken' | 'fixed' | 'no_answer' | 'no_key';

/** Why Google didn't answer (h043): busy (500-503), allowance used up (429), key refused (400, 401, 403), other. */
export type GoogleTrouble = 'busy' | 'allowance' | 'key' | 'other';

export interface GoogleProblem {
  search: 'labelled' | 'plain';
  status: number | null;
  kind: GoogleTrouble;
  /** Google's own short reason, never the address or the key. */
  reason: string;
}

export interface GoogleCheck {
  isbn: string;
  labelled: number | null;
  plain: number | null;
  verdict: GoogleVerdict;
  calls: number;
  /** One entry per call that didn't answer (empty when both answered). */
  problems: GoogleProblem[];
}

export function troubleOf(status: number | null | undefined): GoogleTrouble {
  if (status === 429) return 'allowance';
  if (status === 400 || status === 401 || status === 403) return 'key';
  if (status && status >= 500 && status <= 599) return 'busy';
  return 'other';
}

/** A call that didn't answer, as a problem: its status and Google's reason, with the key removed. */
function problemOf(search: GoogleProblem['search'], r: FetchResult, key: string): GoogleProblem | null {
  if (r.ok) return null;
  const status = typeof r.status === 'number' ? r.status : null;
  const reason = (r.error || '').split(key).join('[key]').slice(0, 200);
  return { search, status, kind: troubleOf(status), reason };
}


const GB = 'https://www.googleapis.com/books/v1/volumes';
/** The labelled search Google stopped answering in early October 2026. */
export const gbLabelledIsbn = (isbn: string, key: string) =>
  `${GB}?q=${encodeURIComponent(`isbn:${isbn}`)}&maxResults=5&printType=books&country=US&key=${encodeURIComponent(key)}`;

const totalOf = (r: FetchResult): number | null => {
  if (!r.ok) return null;
  const t = (r.data as { totalItems?: unknown })?.totalItems;
  return typeof t === 'number' ? t : 0;
};

export function verdictOf(labelled: number | null, plain: number | null): GoogleVerdict {
  if (labelled !== null && labelled > 0) return 'fixed';
  if (labelled === 0 && plain !== null && plain > 0) return 'still_broken';
  return 'no_answer';
}

/** Two Google calls for Dune's ISBN: `isbn:9780441172719` and the plain number. */
export async function checkGoogle(fetchJson: (url: string) => Promise<FetchResult>, googleKey?: string): Promise<GoogleCheck> {
  if (!googleKey) return { isbn: CHECK_ISBN, labelled: null, plain: null, verdict: 'no_key', calls: 0, problems: [] };
  const a = await fetchJson(gbLabelledIsbn(CHECK_ISBN, googleKey));
  const b = await fetchJson(gbByIsbn(CHECK_ISBN, googleKey));
  const labelled = totalOf(a);
  const plain = totalOf(b);
  const problems = [problemOf('labelled', a, googleKey), problemOf('plain', b, googleKey)].filter((p): p is GoogleProblem => p !== null);
  return { isbn: CHECK_ISBN, labelled, plain, verdict: verdictOf(labelled, plain), calls: 2, problems };
}
